/**
 * Tactical HUD Toast & Dialog notification system.
 *
 * Provides non-blocking asynchronous toasts, confirmations, and prompts
 * designed for tactical dark HUD interfaces, replacing synchronous browser dialogs.
 */

export type ToastType = 'info' | 'success' | 'warning' | 'error';

/**
 * Creates or retrieves the root container for floating toast notifications.
 *
 * Why: Ensures all transient HUD toasts are stacked in a single fixed viewport container.
 */
function getOrCreateToastContainer(): HTMLElement {
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.className = 'toast-container';
    document.body.appendChild(container);
  }
  return container;
}

/**
 * Displays a non-blocking tactical HUD toast notification.
 *
 * Why: Replaces synchronous alert() calls to prevent freezing JavaScript execution
 * and disrupting real-time GPS/packet tracking loops during field tracking.
 *
 * @param message - User-facing notification text
 * @param type - Severity type ('info', 'success', 'warning', 'error')
 * @param durationMs - Time in milliseconds before the toast auto-dismisses
 */
export function showToast(message: string, type: ToastType = 'info', durationMs = 3800): void {
  const container = getOrCreateToastContainer();
  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.setAttribute('role', 'alert');

  const indicator = document.createElement('span');
  indicator.className = 'toast-dot';

  const text = document.createElement('span');
  text.className = 'toast-message';
  text.textContent = message;

  toast.appendChild(indicator);
  toast.appendChild(text);
  container.appendChild(toast);

  const dismiss = () => {
    toast.classList.add('toast-fade-out');
    setTimeout(() => {
      toast.remove();
    }, 220);
  };

  toast.addEventListener('click', dismiss);
  setTimeout(dismiss, durationMs);
}

/**
 * Displays an asynchronous tactical confirmation dialog.
 *
 * Why: Provides a non-blocking dark-themed confirmation modal replacing window.confirm(),
 * maintaining operator situational awareness on mobile screens.
 *
 * @param title - Modal header title
 * @param message - Detailed confirmation inquiry
 * @param confirmText - Label for primary affirmation action
 * @param cancelText - Label for dismissal action
 * @returns Promise resolving to true if confirmed, false otherwise
 */
export function showConfirmDialog(
  title: string,
  message: string,
  confirmText = 'Bestätigen',
  cancelText = 'Abbrechen'
): Promise<boolean> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop dialog-backdrop';

    const dialog = document.createElement('div');
    dialog.className = 'tactical-modal tactical-dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');

    dialog.innerHTML = `
      <div class="modal-header">
        <div class="modal-title-group">
          <span class="brand-dot"></span>
          <h3 class="modal-title">${title}</h3>
        </div>
      </div>
      <div class="modal-body" style="padding: 16px 20px;">
        <p style="font-size: 13px; line-height: 1.5; color: var(--text-primary); margin-bottom: 18px;">${message}</p>
        <div style="display: flex; gap: 10px; justify-content: flex-end;">
          <button id="dialog-btn-cancel" class="btn" style="flex: 1;">${cancelText}</button>
          <button id="dialog-btn-confirm" class="btn btn-danger" style="flex: 1;">${confirmText}</button>
        </div>
      </div>
    `;

    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    const cleanup = (result: boolean) => {
      window.removeEventListener('keydown', handleKeyDown);
      backdrop.remove();
      resolve(result);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup(false);
      } else if (e.key === 'Enter') {
        cleanup(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    backdrop.querySelector('#dialog-btn-cancel')?.addEventListener('click', () => cleanup(false));
    backdrop.querySelector('#dialog-btn-confirm')?.addEventListener('click', () => cleanup(true));
  });
}

/**
 * Displays an asynchronous tactical prompt dialog for text input.
 *
 * Why: Replaces window.prompt() with a styled tactical HUD dialog supporting touch input
 * and keyboard submission without disrupting browser rendering.
 *
 * @param title - Modal header title
 * @param message - Prompt description text
 * @param defaultValue - Initial input field content
 * @param confirmText - Label for submit button
 * @returns Promise resolving to entered string or null if canceled
 */
export function showPromptDialog(
  title: string,
  message: string,
  defaultValue = '',
  confirmText = 'Erstellen'
): Promise<string | null> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop dialog-backdrop';

    const dialog = document.createElement('div');
    dialog.className = 'tactical-modal tactical-dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');

    dialog.innerHTML = `
      <div class="modal-header">
        <div class="modal-title-group">
          <span class="brand-dot"></span>
          <h3 class="modal-title">${title}</h3>
        </div>
      </div>
      <div class="modal-body" style="padding: 16px 20px;">
        <p style="font-size: 13px; line-height: 1.5; color: var(--text-primary); margin-bottom: 12px;">${message}</p>
        <input type="text" id="dialog-prompt-input" class="form-input" style="width: 100%; margin-bottom: 16px; background: rgba(0,0,0,0.4); border: 1px solid var(--border-color); color: var(--text-primary); border-radius: 6px; padding: 8px 12px; font-family: var(--font-mono); font-size: 13px;" value="${defaultValue}" />
        <div style="display: flex; gap: 10px; justify-content: flex-end;">
          <button id="dialog-btn-cancel" class="btn" style="flex: 1;">Abbrechen</button>
          <button id="dialog-btn-confirm" class="btn btn-cyan" style="flex: 1;">${confirmText}</button>
        </div>
      </div>
    `;

    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    const input = backdrop.querySelector('#dialog-prompt-input') as HTMLInputElement;
    if (input) {
      input.focus();
      input.select();
    }

    const cleanup = (value: string | null) => {
      window.removeEventListener('keydown', handleKeyDown);
      backdrop.remove();
      resolve(value);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup(null);
      } else if (e.key === 'Enter') {
        cleanup(input.value.trim());
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    backdrop.querySelector('#dialog-btn-cancel')?.addEventListener('click', () => cleanup(null));
    backdrop.querySelector('#dialog-btn-confirm')?.addEventListener('click', () => cleanup(input.value.trim()));
  });
}
